import { Component, DestroyRef, inject } from '@angular/core';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { BeforeUnloadService } from '@app/core/services/before-unload.service';
import {
  ConnectionPointCategoryDto,
  ConnectionPointDto,
  ConnectionPointIdentificationResult,
  ConnectionPointService,
  ConnectionPointAccessKeyDto
} from '@app/api/nts';
import { ControlsOf, FormGroupType } from '@app/core/utils/formgroup-type';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { ToastService } from '@app/core/services/toast.service';
import { Router } from '@angular/router';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { CardComponent } from '@shared/components/card/card.component';
import { GuideCardComponent } from '@shared/components/guide-card/guide-card.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { InputNumberDirective } from '@shared/components/input/number/input-number.directive';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import {
  MeteringpointFormAddressComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-address/meteringpoint-form-address.component';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { createInstallationLocationAddressForm } from '@shared/forms/installation-location-adderss.formgroup';
import { removeNullValues } from '@app/core/utils/remove-null-values';

@Component({
  selector: 'app-connectionpoint-create',
  imports: [
    ButtonDirective,
    CardComponent,
    FormsModule,
    GuideCardComponent,
    HeaderComponent,
    InputDirective,
    InputGroupComponent,
    InputNumberDirective,
    LabelDirective,
    MeteringpointFormAddressComponent,
    ReactiveFormsModule,
    SelectDirective,
    TranslatePipe
  ],
  templateUrl: './connectionpoint-create.component.html',
  styleUrl: './connectionpoint-create.component.css'
})
export class ConnectionpointCreateComponent implements HasUnsavedChanges {
  public id?: string;
  public connectionPoint?: ConnectionPointDto;

  public form: FormGroupType<ConnectionPointDto> = new FormGroup<ControlsOf<ConnectionPointDto>>({
    id: new FormControl(null),
    identification: new FormControl(null),
    alternativeIdentification: new FormControl(null),
    stationNumber: new FormControl(null),
    maximumPower: new FormControl(null),
    maximumCurrent: new FormControl(null),
    description: new FormControl(null),
    address: createInstallationLocationAddressForm(),
    connectionPointCategoryId: new FormControl(null, Validators.required),
    accessKey: new FormGroup<ControlsOf<ConnectionPointAccessKeyDto>>({
      number: new FormControl<string | null>(null),
      placementType: new FormControl<string | null>(null),
      code: new FormControl<string | null>(null),
      remarks: new FormControl<string | null>(null),
      facility: new FormControl<string | null>(null),
    } as any),
  });

  public connectionpointCategories: ConnectionPointCategoryDto[] = [];
  public connectionPointCategoryList: KeyValue[] = [];

  constructor(
    private readonly connectionPointService: ConnectionPointService,
    private readonly toastService: ToastService,
    private readonly router: Router,
  ) {
    inject(BeforeUnloadService).register(() => this.hasUnsavedChanges(), inject(DestroyRef));

    this.connectionPointService.connectionPointCategoriesGet().subscribe((data: ConnectionPointCategoryDto[]) => {
      this.connectionpointCategories = data;
      this.connectionPointCategoryList = data.map(category => ({
        key: category.id ?? '',
        value: category.name ?? ''
      }));
    })

    this.form.get('connectionPointCategoryId')?.valueChanges.subscribe((categoryId: string | null | undefined) => {
      if (categoryId) {
        const selectedCategory = this.connectionpointCategories.find(cat => cat.id === categoryId);
        if (selectedCategory?.suggestedMaximumPower){
          this.form.get('maximumPower')?.setValue(selectedCategory?.suggestedMaximumPower);
        }
        if (selectedCategory?.suggestedMaximumCurrent){
          this.form.get('maximumCurrent')?.setValue(selectedCategory?.suggestedMaximumCurrent);
        }
      }
    });
  }

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  /** Create ConnectionPoint **/
  public create(): void {
    if (this.form.valid) {
      const addressWasDisabled = this.form.get('address')?.disabled;
      if (addressWasDisabled) {
        this.form.get('address')?.enable();
      }

      /** Clean form of null and empty strings **/
      const formValues = removeNullValues(this.form.value);

      /** Create call **/
      this.connectionPointService.connectionPointNewPost(formValues).subscribe({
          next: (data: ConnectionPointIdentificationResult) => {
            this.toastService.show('connectionpoint.connectionpointCreated');
            this.form.markAsPristine();
            if (data.identification) {
              this.router.navigate(['/connectionpoint', data.identification]);
            } else {
              this.router.navigate(['/connectionpoint']);
            }

          }
        }
      );
    } else {
      this.form.markAllAsTouched();
    }
  }

  public cancel(): void {
    this.router.navigate(['/connectionpoint']);
  }
}
